import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CountryList, stateList } from "./states";
import { PaymentApiService } from "src/app/api/payment-api.service";

@Component({
    selector: "app-invoice-dialog",
    templateUrl: "./invoice-dialog.component.html",
    styleUrls: ["./invoice-dialog.component.scss"],
    standalone: false
})
export class InvoiceDialogComponent implements OnInit {
  invoiceForm!: FormGroup;
  states = stateList;
  countries = CountryList;
  district: string[] = [];
  isInternationalPayment = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<InvoiceDialogComponent>,
    private paymentApi: PaymentApiService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.isInternationalPayment = this.data.isInternationalPayment;

    // Initialize form
    this.invoiceForm = this.fb.group({
      hasGST: [false],
      gstNumber: [""],
      country: [""],
      state: [""],
      district: [""],
      addressLine: ["", Validators.required],
      pinCode: [""],
      gymName: [""],
    });

    // Apply conditional validators based on payment type
    this.applyConditionalValidators();

    // Handle GST validation logic
    this.invoiceForm.get("hasGST")?.valueChanges.subscribe((value: boolean) => {
      const gst = this.invoiceForm.get("gstNumber");
      if (value) {
        gst?.setValidators([
          Validators.required,
          Validators.pattern(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/
          ),
        ]);
      } else {
        gst?.clearValidators();
        gst?.setValue("");
      }
      gst?.updateValueAndValidity();
    });

    this.getBillingAddress(this.data.selectedBranchId);
  }

  /**
   * Conditionally set or clear validators
   */
  private applyConditionalValidators(): void {
    const countryCtrl = this.invoiceForm.get("country");
    const stateCtrl = this.invoiceForm.get("state");
    const districtCtrl = this.invoiceForm.get("district");
    const pinCodeCtrl = this.invoiceForm.get("pinCode");

    // Country is always required
    countryCtrl?.setValidators([Validators.required]);

    if (!this.isInternationalPayment) {
      // 🟢 Required for domestic payments
      stateCtrl?.setValidators([Validators.required]);
      districtCtrl?.setValidators([Validators.required]);
      pinCodeCtrl?.setValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]);
    } else {
      // 🔴 Not required for international payments
      stateCtrl?.clearValidators();
      districtCtrl?.clearValidators();
      pinCodeCtrl?.clearValidators();
    }

    // Update control validation state
    countryCtrl?.updateValueAndValidity();
    stateCtrl?.updateValueAndValidity();
    districtCtrl?.updateValueAndValidity();
    pinCodeCtrl?.updateValueAndValidity();
  }

  /**
   * Load saved billing address and populate form
   */
  getBillingAddress(selectedBranchId: string): void {
    this.paymentApi
      .getBillingAddress(this.data.selectedBranchId)
      .then((res) => {
        if (res.data) {
          const country = this.countries.find(
            (country) => country.label === res?.data?.country
          );

          this.invoiceForm.patchValue({
            ...res.data,
            country: country?.value ?? "IN",
          });
        }

        this.loadCities();
      })
      .catch((error) => {
        console.error("Error loading billing address:", error);
      });
  }

  /**
   * Load districts for selected state
   */
  loadCities(): void {
    const state = this.invoiceForm.get("state")?.value;
    this.district = this.states.find((s) => s.state === state)?.districts || [];
  }

  /**
   * Submit form and save billing address
   */
  submitInvoiceDetails(): void {
    if (this.invoiceForm.valid) {
      // Convert country value to label for backend
      const formData = { ...this.invoiceForm.value };
      const selectedCountry = this.countries.find(
        (country) => country.value === formData.country
      );

      this.paymentApi
        .saveBillingAddress({
          ...formData,
          country: selectedCountry.label,
        })
        .then(() => {
          this.dialogRef.close(formData);
        })
        .catch((err) => console.error("Save billing address error:", err));
    } else {
      this.invoiceForm.markAllAsTouched();
    }
  }
}
